/** Provides functions to enroll, start, and bookmark learning paths. */
import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { handleLearningPath } from '@/lib/api/update-user-activity';
import { useAuthUser } from '@/lib/auth/common/useSession';

interface UseLearningPathActionsOptions {
  learningPathUid?: string;
  initialIsSubscribed?: boolean;
  initialIsBookmarked?: boolean;
  initialHasStarted?: boolean;
  onLoginRequired?: () => void;
}

interface UseLearningPathActionsReturn {
  isSubscribed: boolean;
  isBookmarked: boolean;
  hasStarted: boolean;
  isLoading: boolean;
  enroll: () => Promise<boolean>;
  start: () => Promise<boolean>;
  toggleBookmark: () => Promise<boolean>;
  unenroll: () => Promise<boolean>;
}

/** Hook to manage learning path actions. */
export function useLearningPathActions({
  learningPathUid,
  initialIsSubscribed = false,
  initialIsBookmarked = false,
  initialHasStarted = false,
  onLoginRequired,
}: UseLearningPathActionsOptions): UseLearningPathActionsReturn {
  const [isSubscribed, setIsSubscribed] = useState(initialIsSubscribed);
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked);
  const [hasStarted, setHasStarted] = useState(initialHasStarted);
  const [isLoading, setIsLoading] = useState(false);
  // Must be reactive (useAuthUser, not a one-shot isUserLoggedIn() read): otherwise an
  // in-place login (e.g. Impartner SSO, no remount) leaves these callbacks' closures
  // seeing a stale isLoggedIn: false and wrongly reopening the login modal.
  const { isLoggedIn } = useAuthUser();
  const queryClient = useQueryClient();

  // Sync with parent query data when initial values change
  useEffect(() => {
    setIsSubscribed(initialIsSubscribed);
  }, [initialIsSubscribed]);

  useEffect(() => {
    setIsBookmarked(initialIsBookmarked);
  }, [initialIsBookmarked]);

  useEffect(() => {
    setHasStarted(initialHasStarted);
  }, [initialHasStarted]);

  const enroll = useCallback(async (): Promise<boolean> => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return false;
    }

    if (!learningPathUid) return false;

    setIsLoading(true);
    try {
      const response = await handleLearningPath({
        learningPathUid,
        learningPathActivity: { isSubscribed: true },
        queryClient,
      });

      if (response.status === 200 || response.status === 201) {
        setIsSubscribed(true);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, learningPathUid, onLoginRequired, queryClient]);

  const unenroll = useCallback(async (): Promise<boolean> => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return false;
    }

    if (!learningPathUid) return false;

    setIsLoading(true);
    try {
      const response = await handleLearningPath({
        learningPathUid,
        learningPathActivity: { isSubscribed: false },
        queryClient,
      });

      if (response.status === 200 || response.status === 201) {
        setIsSubscribed(false);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, learningPathUid, onLoginRequired, queryClient]);

  const start = useCallback(async (): Promise<boolean> => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return false;
    }

    if (!learningPathUid) return false;

    setIsLoading(true);
    try {
      const response = await handleLearningPath({
        learningPathUid,
        learningPathActivity: { progress: { hasStarted: true } },
        queryClient,
      });

      if (response.status === 200 || response.status === 201) {
        setHasStarted(true);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, learningPathUid, onLoginRequired, queryClient]);

  const toggleBookmark = useCallback(async (): Promise<boolean> => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return false;
    }

    if (!learningPathUid) return false;

    const newBookmarkState = !isBookmarked;

    // Optimistic update
    setIsBookmarked(newBookmarkState);
    setIsLoading(true);

    try {
      const response = await handleLearningPath({
        learningPathUid,
        learningPathActivity: { isBookmarked: newBookmarkState },
        queryClient,
      });

      if (response.status === 200 || response.status === 201) {
        return true;
      }
      setIsBookmarked(!newBookmarkState); // revert on failure
      return false;
    } catch {
      setIsBookmarked(!newBookmarkState); // revert on error
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, isBookmarked, learningPathUid, onLoginRequired, queryClient]);

  return {
    isSubscribed,
    isBookmarked,
    hasStarted,
    isLoading,
    enroll,
    start,
    toggleBookmark,
    unenroll,
  };
}

export default useLearningPathActions;
